/**
 * @fileoverview Ligand-internal nonbonded force kernel.
 *
 * Evaluates the Lennard-Jones (12-6) + Coulomb energy and gradient over every
 * unique ligand atom pair `(i, j)` with `i < j`, applying:
 *
 *   - Exclusions: 1-2 and 1-3 pairs contribute nothing (no LJ, no Coulomb).
 *   - 1-4 scaling (AMBER): LJ scaled by `1/scnb` (= 0.5 with scnb = 2.0),
 *     Coulomb scaled by `1/scee` (= 1/1.2). The two terms get *different*
 *     factors, which is why `pairwise.js` keeps LJ and Coulomb separate.
 *   - Shifted cutoff: pairs beyond `cutoff` are skipped, and the energy of
 *     every retained pair is shifted by `-E(rcut)` so the potential is
 *     continuous (goes to zero) at `r = cutoff`. The shift is a constant in
 *     `r` for fixed parameters, so it does not alter the gradient.
 *
 * The classification of each pair (normal / excluded / 1-4 scaled) uses a
 * sentinel-pointer walk rather than a per-pair hash lookup. For a fixed
 * first-atom `i`, the inner loop's `j` increases
 * monotonically, and the sorted per-atom exclusion / scaled-pair neighbor
 * lists are likewise ascending, so a single advancing pointer per list
 * resolves membership in O(1) amortized per pair.
 *
 * Inputs match the engine contracts:
 *   - `coords`  : flat Float64Array, length 3*N, atom a at [3a .. 3a+2], nm.
 *   - `charges` : Float64Array length N, e (LigandParameters.charges).
 *   - `sigma`   : Float64Array length N, nm (LigandParameters.sigma).
 *   - `epsilon` : Float64Array length N, kJ/mol (LigandParameters.epsilon).
 *   - `exclusionIndex` / `scaledPairIndex`: the sorted packed `Int32Array`
 *     pair lists produced by `topology/exclusions.js`
 *     (`LigandParameters.exclusions` / `.scaledPairs`).
 *   - `grad`    : flat Float64Array, length 3*N. Accumulated into (not
 *     overwritten); the caller zeroes it. nm-space gradient, kJ/mol/nm.
 *
 * Energy is returned as a scalar (kJ/mol). No hot-path allocation: all scratch
 * is module-level.
 *
 * See: pairwise.js (ljPair/coulombPair/combineSigma/combineEps), exclusions.js
 *      (the sorted packed pair lists this walk consumes).
 */

import { combineSigma, ljPair, coulombPair } from './pairwise.js';

/**
 * AMBER 1-4 scaling factors, OpenMM convention (see types.js scee/scnb).
 *   LJ_14   = E_lj   / scnb  with scnb = 2.0  -> multiply by 0.5
 *   COUL_14 = E_coul / scee  with scee = 1.2  -> multiply by 1/1.2
 *
 * These are applied as fixed constants (the WASM port hardcodes the same
 * values), so the kernel does not read the per-ligand scee/scnb divisors carried
 * in the v6 trailer. `getTypedParameters` fails closed if a decoded trailer's
 * divisors differ from SCEE/SCNB, so this fixed 1-4 scaling can never silently
 * mis-scale a force field it doesn't match. SCEE/SCNB are exported for that guard.
 */
export const SCNB = 2.0;
export const SCEE = 1.2;
const LJ_14_SCALE = 1 / SCNB;
const COUL_14_SCALE = 1 / SCEE;

/**
 * Above this cutoff (nm) the potential shift `E(rcut)` is negligible
 * (LJ ~ rcut^-6, Coulomb ~ rcut^-1) and the per-pair shift recompute is
 * skipped. The evaluator drives this kernel with an effectively-infinite
 * cutoff to mirror mm-server's `NonbondedForce::NoCutoff` (evaluator.js
 * NO_CUTOFF = 1e9), so in the deployment path the shift is exactly zero and
 * computing it would just double the pair math. Any physically meaningful
 * cutoff (< ~1e4 nm) still gets the continuous shifted potential.
 * @type {number}
 */
const SHIFT_CUTOFF_MAX = 1e4;

/**
 * Module-level scratch for the pairwise primitives' two-element output.
 * Reused across the entire inner loop so the hot path never allocates.
 * @type {Float64Array}
 */
const out2 = new Float64Array(2);

/**
 * Module-level scratch for per-atom sqrt(epsilon), grown on demand to fit the
 * ligand atom count and reused across calls so the hot path never allocates.
 * Lets the inner loop combine LJ epsilon as sqrtEps_i * sqrtEps_j (a multiply)
 * instead of sqrt(eps_i*eps_j) (a per-pair sqrt). The per-atom epsilons are
 * invariant within an evaluation, so the sqrt is hoisted to an O(N) prepass.
 * Numerically equal to sqrt(eps_i*eps_j) to round-off.
 * @type {Float64Array}
 */
let sqrtEpsScratch = new Float64Array(0);

/**
 * Evaluate the ligand-internal nonbonded energy and accumulate its gradient.
 *
 * The exclusion and scaled-pair lists are the sorted packed flat `Int32Array`s
 * from `exclusions.js`: `[i0, j0, i1, j1, ...]` with each `i < j` and the
 * pairs sorted by the packed key `(i << 16) | j`. Because they are sorted, for
 * a fixed `i` the sublist of pairs starting with `i` is contiguous and its
 * `j` values are ascending — exactly the property the sentinel walk exploits.
 *
 * @param {Float64Array} coords           Flat length 3*N, nm.
 * @param {Float64Array} charges          Length N, e.
 * @param {Float64Array} sigma            Length N, nm.
 * @param {Float64Array} epsilon          Length N, kJ/mol.
 * @param {Int32Array}   exclusionIndex   Sorted packed (i,j) pairs (i<j); 1-2 & 1-3.
 * @param {Int32Array}   scaledPairIndex  Sorted packed (i,j) pairs (i<j); 1-4.
 * @param {number}       cutoff           Nonbonded cutoff radius, nm.
 * @param {Float64Array} grad             Flat length 3*N; accumulated into.
 * @returns {number}                      Nonbonded energy, kJ/mol.
 */
export function evalLigandNonbonded(
    coords, charges, sigma, epsilon, exclusionIndex, scaledPairIndex, cutoff, grad,
) {
    const nAtoms = charges.length;
    const cutoff2 = cutoff * cutoff;

    // Hoist the invariant LJ epsilon-combining sqrt out of the O(N^2) pair loop:
    // precompute sqrt(epsilon) per atom once, then combine via sqrtEps_i*sqrtEps_j.
    if (sqrtEpsScratch.length < nAtoms) sqrtEpsScratch = new Float64Array(nAtoms);
    const sqrtEps = sqrtEpsScratch;
    for (let i = 0; i < nAtoms; i++) sqrtEps[i] = Math.sqrt(epsilon[i]);
    // Skip the per-pair shift recompute when the cutoff is effectively infinite
    // (the NoCutoff deployment path): E(rcut) is then ~0, so shifting is a no-op.
    const applyShift = cutoff < SHIFT_CUTOFF_MAX;

    const nExcl = exclusionIndex.length; // = 2 * nExclPairs
    const nScaled = scaledPairIndex.length; // = 2 * nScaledPairs

    let energy = 0;

    // One advancing pointer per sorted list. Each points at the start (the
    // first-atom slot) of the *next* candidate pair we might consume. As `i`
    // increases across the outer loop, we advance these past any pairs whose
    // first atom is < i (they belong to an already-finished outer index).
    let exclPtr = 0;
    let scaledPtr = 0;

    for (let i = 0; i < nAtoms; i++) {
        // Advance past exclusion / scaled pairs whose first atom is < i. (The
        // sorted ordering guarantees these can never match the current or any
        // later i, so they are dropped exactly once.)
        while (exclPtr < nExcl && exclusionIndex[exclPtr] < i) exclPtr += 2;
        while (scaledPtr < nScaled && scaledPairIndex[scaledPtr] < i) scaledPtr += 2;

        // Per-i cursors into the contiguous sublist of pairs with first atom
        // == i. These walk forward as the inner-loop j increases. They are
        // copies of the shared pointers so the shared pointers themselves only
        // move forward once per outer i (preserving the amortized-O(1) walk).
        let ePtr = exclPtr;
        let sPtr = scaledPtr;

        const i3 = 3 * i;
        const xi = coords[i3];
        const yi = coords[i3 + 1];
        const zi = coords[i3 + 2];
        const qi = charges[i];
        const si = sigma[i];
        const sei = sqrtEps[i];

        for (let j = i + 1; j < nAtoms; j++) {
            // --- Exclusion test via the advancing pointer. ---
            // Skip any (i, j') in the exclusion sublist with j' < j (cannot
            // recur for a larger j). If the next exclusion pair is exactly
            // (i, j), this pair is fully excluded.
            while (ePtr < nExcl && exclusionIndex[ePtr] === i
                   && exclusionIndex[ePtr + 1] < j) {
                ePtr += 2;
            }
            if (ePtr < nExcl && exclusionIndex[ePtr] === i
                && exclusionIndex[ePtr + 1] === j) {
                ePtr += 2; // consume; this pair contributes nothing
                continue;
            }

            // --- 1-4 scaled test via the advancing pointer. ---
            while (sPtr < nScaled && scaledPairIndex[sPtr] === i
                   && scaledPairIndex[sPtr + 1] < j) {
                sPtr += 2;
            }
            let scaled = false;
            if (sPtr < nScaled && scaledPairIndex[sPtr] === i
                && scaledPairIndex[sPtr + 1] === j) {
                scaled = true;
                sPtr += 2;
            }

            // --- Geometry + cutoff. ---
            const j3 = 3 * j;
            const dx = xi - coords[j3];
            const dy = yi - coords[j3 + 1];
            const dz = zi - coords[j3 + 2];
            const r2 = dx * dx + dy * dy + dz * dz;
            // Skip pairs outside the (shifted) cutoff, and skip an
            // exactly-coincident pair (r2 === 0) to avoid a 1/0 singularity
            // that would make LJ/Coulomb diverge to +/-Inf.
            if (r2 >= cutoff2 || r2 === 0) continue;

            const ljScale = scaled ? LJ_14_SCALE : 1;
            const coulScale = scaled ? COUL_14_SCALE : 1;

            let gOverR = 0;

            // --- Lennard-Jones. ---
            const sigmaIJ = combineSigma(si, sigma[j]);
            const epsIJ = sei * sqrtEps[j];
            if (epsIJ !== 0 && sigmaIJ !== 0) {
                ljPair(r2, sigmaIJ, epsIJ, out2);
                const eLj = out2[0];
                const gLj = out2[1];
                // Shifted cutoff: subtract E_lj(rcut) so the potential is
                // continuous at r = cutoff. The shift is constant in r, so the
                // gradient factor gLj is untouched. Skipped (== 0) for the
                // effectively-infinite NoCutoff path.
                let eLjShift = 0;
                if (applyShift) {
                    ljPair(cutoff2, sigmaIJ, epsIJ, out2);
                    eLjShift = out2[0];
                }
                energy += ljScale * (eLj - eLjShift);
                gOverR += ljScale * gLj;
            }

            // --- Coulomb. ---
            const qq = qi * charges[j];
            if (qq !== 0) {
                coulombPair(r2, qq, out2);
                const eCoul = out2[0];
                const gCoul = out2[1];
                // Same shifted-cutoff treatment for the Coulomb term (skipped
                // for the effectively-infinite NoCutoff path).
                let eCoulShift = 0;
                if (applyShift) {
                    coulombPair(cutoff2, qq, out2);
                    eCoulShift = out2[0];
                }
                energy += coulScale * (eCoul - eCoulShift);
                gOverR += coulScale * gCoul;
            }

            // --- Accumulate the gradient. ---
            // dE/dx_i = gOverR * (x_i - x_j); the contribution on j is its
            // negative (Newton's third law).
            if (gOverR !== 0) {
                const gx = gOverR * dx;
                const gy = gOverR * dy;
                const gz = gOverR * dz;
                grad[i3] += gx;
                grad[i3 + 1] += gy;
                grad[i3 + 2] += gz;
                grad[j3] -= gx;
                grad[j3 + 1] -= gy;
                grad[j3 + 2] -= gz;
            }
        }
    }

    return energy;
}
